import classnames from 'classnames';
import { DateTime } from 'luxon';
import * as React from 'react';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import "react-widgets/styles.css";
import {
    Button, Col, Collapse, FormGroup, Input, Label, Nav, NavItem,
    NavLink, Row, Spinner, TabContent, TabPane
} from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../hooks';
import { Node, EntityReference, selectOENode, selectSDNode, getAllEntities, selectEntities } from "../redux/EntitiesSlice";
import {
    Affiliation, DoingBusinessAs, Entity, AffiliationInput, EntityInput, EntityReportAccessChangeInput, getSelectedEntity,
    saveEntityDetail, selectedEntityAddress, isEntityCode
} from '../redux/EntitySlice';
import { Part, Reference } from '../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion } from '../redux/TaxonomyVersionSlice';
import utilityHelper from "../utils/utilityHelper";
import Page from './Page';
import AddressEditor from './feature/AddressEditor';
import DistrictTypeManager from "./feature/DistrictTypeManager";
import { StyledSelectableListItem, addNode } from "./feature/DistrictTypeManager";
import EntityContactsManager from './feature/EntityContactsManager';
import EntityCreator from "./feature/EntityCreator";
import EntityHistoryViewer from './feature/EntityHistoryViewer';
import EntityStatusEditor from './feature/EntityStatusEditor';
import FiscalYearEditor from './feature/FiscalYearEditor';
import ReportAccessEnabler from "./feature/ReportAccessEnabler";
import dateHelper from '../utils/dateHelper';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import { County } from '../redux/AddressSlice';
import Swal from 'sweetalert2';

const EntityManagementAdmin = () => {

    const dispatch = useAppDispatch();
    const entityAddressSelector = useAppSelector(selectedEntityAddress);
    const entitySelector: Entity | null = useAppSelector(getSelectedEntity);
    const selectedVersion: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    const entitiesSelector: EntityReference[] | null = useAppSelector(selectEntities);
    const specialDistrictNodeSelector: Node | null = useAppSelector(selectSDNode);
    const otherEntityNodeSelector: Node | null = useAppSelector(selectOENode);
    
    //fiscal due date hooks
    const [fiscalDay, setFiscalDay] = React.useState<number | null>(null);
    const [fiscalMonth, setFiscalMonth] = React.useState<number | null>(null);

    //entity status hooks
    const [dissolvedDate, setDissolvedDate] = React.useState<string | null>(null);
    const [entityStatus, setEntityStatus] = React.useState<string | null>(null);
    const [inactiveDate, setInactiveDate] = React.useState<string | null>(null);
    const [reactivateDate, setReactivateDate] = React.useState<string | null>(null);
    const [statusReason, setStatusReason] = React.useState<string | null>(null);
    const [renamedDate, setRenamedDate] = React.useState<string | null>(null);
    const [mergedDate, setMergedDate] = React.useState<string | null>(null);
    
    const [mergedEntityStatusDisabled, setMergedEntityStatusDisabled] = React.useState<boolean>(false);
//address hooks
    const [street, setStreet] = React.useState<string | null>(null);
    const [zipCode, setZipCode] = React.useState<string | null>(null);
    const [pobox, setPobox] = React.useState<string | null>(null);
    const [state, setState] = React.useState<string | null>(null);
    const [county, setCounty] = React.useState<string | null>(null);
    const [cityCode, setCityCode] = React.useState<string | null>(null);
    const [servesInCountyCodes, setServesInCountyCodes] = React.useState<Array<number> >([]);

    //visual hook
    const [activeTab, setActiveTab] = React.useState('1');
    const [isEntityUpdateSaving, setIsEntityUpdateSaving] = React.useState<boolean>(false);

    //error hook
    const [macroError, setMacroError] = React.useState<any>();

    //label hooks
    const [entityName, setEntityName] = React.useState<string>('');
    const [nameChangeDate, setNameChangeDate] = React.useState<Date | null>(null);
    const [primaryChangeDate, setPrimaryChangeDate] = React.useState<Date | null>(null);
    const [entityTypeCode, setEntityTypeCode] = React.useState<string>('');
    const [districtType, setDistrictType] = React.useState<string | null>(null);
    const [entityCode, setEntityCode] = React.useState<string | null>('');
    const [createDate, setCreateDate] = React.useState<string | null>(null);
    const [primaryGovernment, setPrimaryGovernment] = React.useState<string | null>(null);
    const [districtDependencyStatus, setDistrictDependencyStatus] = React.useState<string>('');
    const [depStatusChangeDate, setDepStatusChangeDate] = React.useState<Date | null>(null);
    const defaultOption: { value: string; label: string } = { value: "0", label: "Select..." };
    const [selectedPrimary, setSelectedPrimary] = React.useState<{ value: string; label: string }>(defaultOption);

    //report access hooks
    const [parentAfrEnabled, setParentAfrEnabled] = React.useState<boolean>(false);
    const [parentCourtReporting939Enabled, setParentCourtReporting939Enabled] = React.useState<boolean>(false);
    const [parentCourtReporting29Enabled, setParentCourtReporting29Enabled] = React.useState<boolean>(false);
    const [parentAfrReportAccessDate, setParentAfrReportAccessDate] = React.useState<any>(null);
    const [parentNineThirtyNineReportAccessDate, setParentNineThirtyNineReportAccessDate] = React.useState<any>(null);
    const [parentTwentyNineReportAccessDate, setParentTwentyNineReportAccessDate] = React.useState<any>(null);
    const [parentReportAccessHasChanged, setParentReportAccessHasChanged] = React.useState<boolean>(false);


    //references
    const [creationDocumentData, setCreationDocumentData] = React.useState<Array<string>>([]);
    const [statoryAuthorityData, setStatoryAuthorityData] = React.useState<Array<string>>([]);

    //doing business as
    const [doingBusinessAs, setDoingBusinessAs] = React.useState<"Previously Doing Business As" | "Currently Doing Business As" | "">("");
    const [shouldShowDoingBusinessAs, setShouldShowDoingBusinessAs] = React.useState<Boolean>(false);
    const [doingBusinessAsOptions, setDoingBusinessAsOptions] = React.useState<Array<{ value: string; label: string; }>>([]);
    const [selectedDoingBusinessAs, setSelectedDoingBusinessAs]  
        = React.useState<{ value: string; label: string }>(defaultOption);
    const [primaryOptions, setPrimaryOptions] = React.useState<Array<{ value: string; label: string; }>>([]);
    const [districtTypeOptions, setDistrictTypeOptions] = React.useState<Array<{ value: string; label: string; }>>([]);
    const [hideSelectedOptions, setHideSelectedOptions] = React.useState<Boolean>(false);


    function initEntityReferenceGivenSelector(setReferenceHook: React.Dispatch<React.SetStateAction<string[]>>, referenceType: string) {

        if (entitySelector && entitySelector.references) {
            if (entitySelector.references.length > 0) {
                let result: Array<string> = [];

                var referenceNodeList = entitySelector.references.filter((referenceNode: Reference) => referenceNode.type.indexOf(referenceType) > 0);

                if (referenceNodeList.length > 0) {

                    for (var i = 0; i < referenceNodeList.length; i++) {

                        let workingLabel: string = "";
                        var namePartNode = referenceNodeList[i].parts.find((element: Part) => element.definition.name.toLowerCase() == "name");

                        if (namePartNode) {
                            workingLabel += namePartNode.value + ": ";
                        }

                        var remainingPartNodes = referenceNodeList[i].parts.filter((element: Part) => element.definition.name.toUpperCase() != "NAME"
                            && element.definition.name.toUpperCase() != "UASID");

                        for (var j = 0; j < remainingPartNodes.length; j++) {
                            workingLabel += remainingPartNodes[j].definition.name + " " + remainingPartNodes[j].value + "<br />";
                        }

                        result.push(workingLabel);
                    }

                    setReferenceHook(result);
                }
            }
        }
    }

    const handleDepStatusChange = function (event: any) {
        setDistrictDependencyStatus(event.target.value);
    }

    const handleDoingBusinessAsChange = (selectItem: { value: string; label: string }) => {
        setSelectedDoingBusinessAs(selectItem);
    }

    const handlePrimaryChange = (selectItem: { value: string; label: string }) => {
        setSelectedPrimary(selectItem);
    }

    const toggle = (tab: any) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    function getDoingBusinessAsAffiliation(): DoingBusinessAs | null {
        let result: DoingBusinessAs | null = null;

        if (entitySelector) {
            if (selectedDoingBusinessAs.label != defaultOption.label) {
                switch (doingBusinessAs) {

                    case "Currently Doing Business As":
                        result = {
                            currentEntity: selectedDoingBusinessAs.value,
                            previousEntity: entitySelector.code
                        }
                        break;

                    case "Previously Doing Business As":
                        result = {
                            previousEntity: selectedDoingBusinessAs.value,
                            currentEntity: entitySelector.code
                        }
                        break;

                    default:
                        break;
                }
            }
        }

        return result;
    }

    const convertDate = function (dString: string | null): string | null {
        if (dString && dString.length > 0) {
            if (dString.indexOf("/") > 0) {
                return DateTime.fromFormatParser(dString,DateTime.DATE_SHORT).toISO();
            }
            return DateTime.fromISO(dString).toISO();
        }
        return null;
    }
    const saveEntity = async () => {
        setIsEntityUpdateSaving(true);
        var convertedDissolvedDate = convertDate(dissolvedDate);
        // NOTE - we have to convert from the "Z" suffix to the GMT offset model in order for GraphQL to parse the date.
        var convertedNameChangeDate: string | null = nameChangeDate ? DateTime.fromJSDate(nameChangeDate).toISO().replace("Z","-04:00") : null;
        var convertedDepStatusChangeDate: string | null = depStatusChangeDate ? DateTime.fromJSDate(depStatusChangeDate).toISO().replace("Z", "-04:00") : null;
        var convertedInactiveDate = convertDate(inactiveDate);
        var convertedReactivateDate = convertDate(reactivateDate);
        var convertedRenamedDate = convertDate(renamedDate);
        var convertedMergedDate = convertDate(mergedDate);
        var doingBusiness = getDoingBusinessAsAffiliation();
        const currentDate = new Date();
        
        let isCurrentDate = dateHelper.formatDateToISO(currentDate.toISOString());
        let checkCurrentDate = convertDate(isCurrentDate);
        
        if (entityStatus == "MERGED") {
            if (statusReason === null) {
                Swal.fire("Merged Reason is empty. Please enter reason.");
                setIsEntityUpdateSaving(false);
                return;
            } else if (doingBusiness === null) {
                Swal.fire("You must choose an entity from the Currently Doing Business As drop - down.");
                setIsEntityUpdateSaving(false);
                return;
            }
        }
        
       if (entityStatus == "RENAMED") {
            if (doingBusiness === null) {
                Swal.fire( "You must choose an entity from the Currently Doing Business As drop - down.");
                setIsEntityUpdateSaving(false);
                return;
            }
        }

        var affiliationUpdate: AffiliationInput | null = null;
        if (primaryChangeDate && entitySelector) {
            var convertedPrimaryChange: string = DateTime.fromJSDate(primaryChangeDate).toISO().replace("Z", "-04:00");
            affiliationUpdate = {
                affiliate: {
                    code: selectedPrimary.value,
                    name: selectedPrimary.label,
                    id: "",
                    category: ""
                },
                affiliateId: selectedPrimary.value,
                effectiveDate: convertedPrimaryChange,
                endDate: null
            }
        }


        let entityReportAccessChangesFromUI: EntityReportAccessChangeInput[] = [{
            state: 'AFR_ENABLED',
            value: parentAfrEnabled,
            timestamp: parentAfrReportAccessDate ? parentAfrReportAccessDate : null
        },
        {
            state: 'COURT_REPORTING_939_ENABLED',
            value: parentCourtReporting939Enabled,
            timestamp: parentNineThirtyNineReportAccessDate ? parentNineThirtyNineReportAccessDate : null
        },
        {
            state: 'COURT_REPORTING_29_ENABLED',
            value: parentCourtReporting29Enabled,
            timestamp: parentTwentyNineReportAccessDate ? parentTwentyNineReportAccessDate : null
        }];

        // NOTE - the name is now drawn from the local state rather than being re-fetched from the entity. This allows for the user to change its value.
        let entityToSave: EntityInput = {
            entityId: entitySelector !== null ? entitySelector.code : "",
            fiscalEndMonth: fiscalMonth,
            fiscalEndDay: fiscalDay,
            dissolvedDate: convertedDissolvedDate,
            inactiveDate: convertedInactiveDate,
            createDate: convertedReactivateDate,
            renamedDate: convertedRenamedDate,
            mergedDate: convertedMergedDate,
            affiliation: affiliationUpdate,
            status: entityStatus,
            statusReason: statusReason && statusReason.length > 0 ? statusReason : null,
            address: {
                street: street,
                pobox: pobox,
                cityId: cityCode,
                zip: zipCode,
            },
            name: entitySelector !== null ? entityName : "",
            nameChangeDate: entitySelector !== null ? convertedNameChangeDate : null,
            typecode: entitySelector !== null ? entitySelector.typecode : "",
            districttype: districtType,
            taxonomyYear: selectedVersion ? selectedVersion.year : null,
            entityReportAccessChangeInput: parentReportAccessHasChanged == true ? entityReportAccessChangesFromUI : null,
            doingBusinessAs: getDoingBusinessAsAffiliation(),
            districtDependency: entitySelector !== null ? districtDependencyStatus : "",
            districtDependencyChangeDate: entitySelector !== null ? convertedDepStatusChangeDate : null,
            servesInCounties: servesInCountyCodes
        }

        await dispatch(saveEntityDetail(entityToSave)).then((resultAction: any) => {
            if (saveEntityDetail.rejected.match(resultAction)) {
                setMacroError(resultAction.error);
            }
            else {
                setMacroError(null);
            }
        });

        await dispatch(getAllEntities());

        setIsEntityUpdateSaving(false);
    }
    function initPrimaryOptions(entitiesSelector: EntityReference[] | null) {
        if (entitiesSelector) {

            var result: Array<{ value: string; label: string; }> = [];
            result.push({ label: "End Primary Relationship", value: "0" });
            entitiesSelector.map( (entityRef : EntityReference) => {
                result.push({ value: entityRef.code, label: entityRef.code + " - " + entityRef.name });
            })
            setPrimaryOptions(result);
        } else {
            setPrimaryOptions([]);
        }
    }
    function findNodeByName(current: Node | null, nodeName: string): Node | null {
        if (!current) {
            return null;
        }
        if (current.concept) {
            if (current.concept.name === nodeName) {
                return current;
            }
        }
        var next: Node | null = null;
        if (current.outgoing && current.outgoing.length > 0) {
            for (var child of current.outgoing) {
                next = findNodeByName(child.to, nodeName);
                if (next) {
                    return next;
                }
            }
        }
        return null;
    }
    function findOptionByName(current: StyledSelectableListItem | null, nodeName: string): StyledSelectableListItem | null {
        if (!current) {
            return null;
        }
        if (current.value) {
            if (current.value === nodeName) {
                return current;
            }
        }
        var next: StyledSelectableListItem | null = null;
        if (current.children && current.children.length > 0) {
            for (var child of current.children) {
                next = findOptionByName(child, nodeName);
                if (next) {
                    return next;
                }
            }
        }
        return null;
    }
    function getDistrictTypeRootNode(): Node | null {
        if (!entitySelector) {
            return null;
        }
        if (entitySelector.code < "3" || entitySelector.code >= "9") {
            return null;
        }
        if (entitySelector.code < "5") {
            return specialDistrictNodeSelector;
        } else {
            return otherEntityNodeSelector;
        }
    }
    function initDistrictTypeOptions(dtSelector : Node | null) {
        if( dtSelector) {
            var result: Array<{ value: string; label: string; }> = [];
                dtSelector.outgoing.map(e => {
                    //TODO Note: I'm putting true here to resolve the typescript errors
                    //but I really don't know the implications of this.
                    var rootNode: Node = e.to;
                    if (rootNode.concept && !isEntityCode(rootNode.concept.code)) {
                        var rootItem: StyledSelectableListItem = addNode(null, e.to, false);
                        rootItem.children.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
                        result.push(rootItem);
                        if (districtType && districtType.length > 0) {
                            var dtRootNode: Node | null = getDistrictTypeRootNode();
                            if (dtRootNode) {
                                var nFound: Node | null = findNodeByName(dtRootNode, districtType);
                                if (nFound) {
                                    var selItem = findOptionByName(rootItem, nFound.concept.id);
                                    if (selItem) {
                                        selItem.checked = true;
                                    }
                                    var craItem = findOptionByName(rootItem, "SD_CommunityRedevelopmentAgencies");
                                    if (craItem) {
                                        craItem.disabled = true;
                                    }
                                }
                            }
                        }
                    }
                });
            setDistrictTypeOptions(result);
        } else {
            setDistrictTypeOptions([]);
        }
    }
    const handleDistrictTypeChange = function (currentNode: any, selectedNodes: any[]) {
        if (selectedNodes && selectedNodes.length > 0) {
            setDistrictType(selectedNodes[0].label);
        } else {
            if (entitySelector) {
                if (entitySelector.code < "5") {
                    setDistrictType("Special District");
                } else {
                    setDistrictType("Other Entity");
                }
            } else {
                setDistrictType(null);
            }
        }
    }
    function initDoingBusinessAsOptions(entitiesSelector: EntityReference[] | null, doingBusinessAs: string, setDoingBusinessAsOptions: React.Dispatch<React.SetStateAction<{ value: string; label: string; }[]>>) {
        if (entitiesSelector) {

            var result: Array<{ value: string; label: string; }> = [];
            var updatedResults: Array<{ value: string; label: string; }> = [];
            
            var currentEntityCode = entitySelector && entitySelector.code ? entitySelector.code : 0;
            var mergedEntites = entitySelector && entitySelector.mergedEntities ? entitySelector.mergedEntities : null;
            let entityListWithStatus: EntityReference[] = [];
            result.push({ label: "Remove Relationship", value: "0" });
            
            switch (doingBusinessAs) {
                case "Currently Doing Business As":
                    entityListWithStatus = entitiesSelector.filter((element: EntityReference) => element.status == "ACTIVE" && currentEntityCode != element.code);
                    for (var i = 0; i < entityListWithStatus.length; i++) {
                        result.push({ value: entityListWithStatus[i].code, label: entityListWithStatus[i].code + " - " + entityListWithStatus[i].name });
                    }
                    break;
                case "Previously Doing Business As":
                    entityListWithStatus = entitiesSelector.filter((element: EntityReference) => (element.status == "DISSOLVED"
                        || element.status == "INACTIVE" || element.status == "RENAMED" || element.status == "MERGED")  && currentEntityCode != element.code );
                    for (var i = 0; i < entityListWithStatus.length; i++) {
                        result.push({
                            value: entityListWithStatus[i].code, label: entityListWithStatus[i].code
                                + " - " + entityListWithStatus[i].name + " (" + entityListWithStatus[i].status + ")"
                        });
                    }
                    break;
                default:
                    entityListWithStatus = [];
                    setDoingBusinessAsOptions([]);
                    setSelectedDoingBusinessAs(defaultOption);
                    break;
            }

            if (result.length > 1) {

                var activeEntites = entitiesSelector.filter((element: EntityReference) => element.status == "ACTIVE" && currentEntityCode != element.code);
                for (var i = 0; i < activeEntites.length; i++) {
                    updatedResults.push({ value: activeEntites[i].code, label: activeEntites[i].code + " - " + activeEntites[i].name });
                }

                setDoingBusinessAsOptions(updatedResults);
                //setDoingBusinessAsOptions(result);

                let codeValue: string | null = null;
                let tempLable1: string | undefined | null = "";
                let tempLable2: string | undefined | null = "";
                let tempLable3: string | undefined | null = "";
                let tempLableCode: string | undefined | null = "";

                if (entitySelector && entitySelector.doingBusinessAs) {
                    let currentCodeValue: string | null = entitySelector.doingBusinessAs.currentEntity;
                    let previousCodeValue: string | null = entitySelector.doingBusinessAs.previousEntity;

                    if (entitySelector.code == currentCodeValue) {
                        codeValue = entitySelector.doingBusinessAs.previousEntity;
                    } else if (entitySelector.code == previousCodeValue) {
                        codeValue = entitySelector.doingBusinessAs.currentEntity;
                    }

                    var foundRelatingListItem = result.find((element: { value: string; label: string; }) => element.value == codeValue);

                    if (entitySelector.mergedEntities && entitySelector.mergedEntities.length > 0) {


                        if (entityStatus == "MERGED" || entityStatus == "DISSOLVED" || entityStatus == "INACTIVE" || entityStatus == "RENAMED") {

                            entitySelector.mergedEntities.forEach((item, index) => {
                                if (item.currentEntity != currentEntityCode) {
                                    entityListWithStatus = entitiesSelector.filter((element: EntityReference) => (element.code == item.currentEntity));
                                    for (var i = 0; i < entityListWithStatus.length; i++) {
                                        result.push({
                                            value: entityListWithStatus[i].code, label: entityListWithStatus[i].code
                                                + " - " + entityListWithStatus[i].name + " (" + entityListWithStatus[i].status + ")"
                                        });
                                    }
                                    foundRelatingListItem = result.find((element: { value: string; label: string; }) => element.value == item.currentEntity);
                                }
                            });
                        }
                        else {
                                mergedEntites = entitySelector.mergedEntities;
                                mergedEntites.forEach((item, index) => {
                                let foundMergedRelatingListItem = entitiesSelector.find(x => x.code == item.previousEntity);

                                if (entitySelector.isParentEntityActive == true) {
                                    let parentEntityMergedItem = entitiesSelector.find(x => x.code == item.currentEntity);
                                    foundMergedRelatingListItem = parentEntityMergedItem;
                                }
                                else {
                                    let noParentEntityItem = entitiesSelector.find(x => x.code == item.previousEntity);
                                    foundMergedRelatingListItem = noParentEntityItem;
                                }

                                if (foundMergedRelatingListItem) {
                                    tempLableCode = foundMergedRelatingListItem.code;
                                    tempLable1 = foundMergedRelatingListItem.code
                                        + " - " + foundMergedRelatingListItem.name + " (" + foundMergedRelatingListItem.status + ")"
                                    tempLable2 = `${tempLable2} ${", "} ${tempLable1}`;
                                }
                                if (tempLable2) {
                                    tempLable3 = tempLable2.substring(2);

                                    if (foundRelatingListItem) {
                                        foundRelatingListItem.label = tempLable3;
                                    }
                                    else {
                                        defaultOption.label = tempLable3;
                                    }
                                }
                            })
                        }
                    }
                    if (foundRelatingListItem) {
                        handleDoingBusinessAsChange(foundRelatingListItem);
                    } else {
                        defaultOption.label = "";
                        handleDoingBusinessAsChange(defaultOption);
                    }
                } else {
                    defaultOption.label = "";
                    handleDoingBusinessAsChange(defaultOption);
                }
            }

        } else {
            setDoingBusinessAsOptions([]);
        }
    }

    const createChangeDate = function (event: any, existing : Date | null) {
        if (event && isNaN(Date.parse(event)) == false) {
            var dateString = dateHelper.formatDateToISO(event);
            if (!existing || dateString !== existing.toISOString()) {
                return new Date(dateString);
            } else {
                return existing;
            }
        } else {
            return null;
        }
    }
    const handleNameChangeDate = function (event: any) {
        setNameChangeDate(createChangeDate(event, nameChangeDate));
    }

    const handleDepStatusChangeDate = function (event: any) {
        setDepStatusChangeDate(createChangeDate(event, depStatusChangeDate));
    }

    const handlePrimaryChangeDate = function (event: any) {

        if (event && isNaN(Date.parse(event)) == false) {
            var dateString = dateHelper.formatDateToISO(event);
            if (!primaryChangeDate || dateString !== primaryChangeDate.toISOString()) {
                setPrimaryChangeDate(new Date(dateString));
            }
        } else {
            setNameChangeDate(null);
        }
    }



    React.useEffect(() => {
        if (entityAddressSelector) {
            if (entityAddressSelector.street) {
                setStreet(entityAddressSelector.street);
            }
            if (entityAddressSelector.zip) {
                setZipCode(entityAddressSelector.zip);
            }
            if (entityAddressSelector.pobox) {
                setPobox(entityAddressSelector.pobox);
            }
            if (entityAddressSelector.city &&
                entityAddressSelector.city.code) {
                setCityCode(entityAddressSelector.city.code);
            }
            setState(null);
            setCounty(null);

        }
        else {
            setStreet(null);
            setZipCode(null);
            setPobox(null);
            setState(null);
            setCounty(null);
            setCityCode(null);
        }
    }, [entityAddressSelector])

    React.useEffect(() => {

        setCreationDocumentData([]);
        setStatoryAuthorityData([]);
        setPrimaryChangeDate(null);

        if (entitySelector) {
            setDissolvedDate(entitySelector.dissolvedDate);
            setInactiveDate(entitySelector.inactiveDate);
            setReactivateDate(entitySelector.creationDate);
            setRenamedDate(entitySelector.renamedDate);
            setMergedDate(entitySelector.mergedDate);
            setFiscalMonth(entitySelector.fiscalEndMonth);
            setFiscalDay(entitySelector.fiscalEndDay);
            setEntityStatus(entitySelector.status);
            setEntityName(entitySelector.name);
            setEntityTypeCode(entitySelector.typecode);
            setDistrictType(entitySelector.districttype);
            setCreateDate(DateTime.fromISO(entitySelector.creationDate).toFormat('LL/dd/yyyy'));

            if (entitySelector && entitySelector.servesInCounties && entitySelector.servesInCounties.length > 0) {
                let countyCodeNumbers: Array<number> = [];
                for (var i = 0; i < entitySelector.servesInCounties.length; i++) {  
                    var codeString = entitySelector.servesInCounties[i].code;
                    if (codeString) {
                        let codeNum = parseInt(codeString);
                        countyCodeNumbers.push(codeNum);
                    }                   
                }
                if (countyCodeNumbers.length > 0) {
                    setServesInCountyCodes(countyCodeNumbers);
                }
            } else {
                setServesInCountyCodes([]);
            }
            
            setEntityCode(entitySelector.code);
            if (entitySelector.affiliation) {
                var aff: Affiliation = entitySelector.affiliation;
                var hasPrimary: boolean = false;
                if (aff.endDate) {
                    hasPrimary = dateHelper.IsDateInFuture(aff.endDate);
                } else {
                    hasPrimary = true;
                }
                if (hasPrimary) {
                    setPrimaryGovernment(entitySelector.affiliation.affiliate.name);
                    setSelectedPrimary({ value: entitySelector.affiliation.affiliate.code, label: entitySelector.affiliation.affiliate.code + " - " + entitySelector.affiliation.affiliate.name });
                }
            }

            initEntityReferenceGivenSelector(setCreationDocumentData, "/creation");
            initEntityReferenceGivenSelector(setStatoryAuthorityData, "/authority");
            setStatusReason(entitySelector.statusReason);
            setDistrictDependencyStatus(entitySelector.districtDependency);
            setDepStatusChangeDate(null);
            setMergedEntityStatusDisabled(entitySelector.status == "MERGED" ? false : true);
        }
        else {
            setDissolvedDate(null);
            setInactiveDate(null);
            setFiscalMonth(null);
            setReactivateDate(null);
            setRenamedDate(null);
            setMergedDate(null);
            setFiscalDay(null);
            setEntityStatus(null);
            setEntityName("");
            setEntityTypeCode("");
            setDistrictType(null);
            setEntityCode(null);
            setCreateDate(null);
            setPrimaryGovernment(null);
            setSelectedPrimary(defaultOption);
            setStatusReason("");
            setDistrictDependencyStatus("NONE");
            setDepStatusChangeDate(null);
            setServesInCountyCodes([]);
            setMergedEntityStatusDisabled(false);
        }

    }, [entitySelector]);

    React.useEffect(() => {
        initPrimaryOptions(entitiesSelector);
        initDoingBusinessAsOptions(null, "", setDoingBusinessAsOptions);
    }, [entitiesSelector]);


    React.useEffect(() => {
        initDistrictTypeOptions(getDistrictTypeRootNode());
    }, [districtType])

    React.useEffect(() => {

        if (entityStatus) {
            if (entityStatus == "DISSOLVED" || entityStatus == "INACTIVE" || entityStatus == "RENAMED" || entityStatus == "MERGED") {
                setDoingBusinessAs("Currently Doing Business As");
                setShouldShowDoingBusinessAs(true);
                setHideSelectedOptions(false);
              
            }
            else if (entityStatus == "ACTIVE") {
                setDoingBusinessAs("Previously Doing Business As");
                setShouldShowDoingBusinessAs(true);
                setHideSelectedOptions(true);
                
                setMergedEntityStatusDisabled(true);
                defaultOption.label = "";
            }
            else {
                setDoingBusinessAs("");
                setShouldShowDoingBusinessAs(false);
            }
        } else {
            setDoingBusinessAs("");
            setShouldShowDoingBusinessAs(false);
        }
        //dispatch(getAllEntities());

    }, [entityStatus])

    React.useEffect(() => {
        initDoingBusinessAsOptions(entitiesSelector, doingBusinessAs, setDoingBusinessAsOptions);
    }, [doingBusinessAs])



    React.useEffect(() => {

        let newDoingBusinessAsString: "Previously Doing Business As" | "Currently Doing Business As" | "" = "";

        if (entitySelector) {
            if (entitySelector.status) {
                if (entitySelector.status == "DISSOLVED" || entitySelector.status == "INACTIVE" || entitySelector.status == "RENAMED" || entitySelector.status == "MERGED") {
                    newDoingBusinessAsString = "Currently Doing Business As";
                } else if (entitySelector.status == "ACTIVE") {
                    newDoingBusinessAsString = "Previously Doing Business As";
                }
            }
            initDoingBusinessAsOptions(entitiesSelector, newDoingBusinessAsString, setDoingBusinessAsOptions);
        }

    }, [entitiesSelector]  )

    const creationDocumentLabelRender = creationDocumentData.length > 0 ? (<React.Fragment>
        <FormGroup>
            <Row>
                <Col md="4" style={{ textAlign: "left" }}>
                    <Label>Creation Documents:</Label>
                </Col>
                <Col md="8">
                    <ul>
                        {creationDocumentData.map((element: string, index) => (
                            <li key={index}>
                                <div dangerouslySetInnerHTML={{ __html: element }} ></div>
                            </li>
                        ))}
                    </ul>
                </Col>
            </Row>
        </FormGroup>

    </React.Fragment>) : (<></>);

    const statutoryAuthorityLabelRender = statoryAuthorityData.length > 0 ? (<React.Fragment>
        <FormGroup>
            <Row>
                <Col md="4" style={{ textAlign: "left" }}>
                    <Label>Statutory Authority:</Label>
                </Col>
                <Col md="8">
                    <ul>
                        {statoryAuthorityData.map((element: string, index) => (
                            <li key={index}>
                                <div dangerouslySetInnerHTML={{ __html: element }} ></div>
                            </li>
                        ))}
                    </ul>
                </Col>
            </Row>
        </FormGroup>
    </React.Fragment>) : (<></>);

    return (
        <Page error={macroError} setError={setMacroError}>
            <Nav tabs>
                <NavItem>
                    <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => { toggle('1'); }}>
                        Edit Users
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => { toggle('2'); }}>
                        Edit Entity Information
                    </NavLink>
                </NavItem>
                <NavItem className="mr-auto">
                    <NavLink
                        className={classnames({ active: activeTab === '3' })}
                        onClick={() => { toggle('3'); }}>
                        Entity History
                    </NavLink>
                </NavItem>

                <NavItem >
                    <NavLink
                        className={classnames({ active: activeTab === '4' })}
                        onClick={() => { toggle('4'); }}>
                        Create Entity
                    </NavLink>
                </NavItem>
                <NavItem >
                    <NavLink
                        className={classnames({ active: activeTab === '5' })}
                        onClick={() => { toggle('5'); }}>
                        Manage District Types
                    </NavLink>
                </NavItem>
            </Nav>
            <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                    <Row>
                        <Col sm="12">
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <EntityContactsManager isStaff={false} />
                        </Col>
                    </Row>
                </TabPane>
                <TabPane tabId="2">
                    <Row>
                        <Col md="12">
                            <br />
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Row>
                                    <Col md="4" style={{ textAlign: "left" }}>
                                        <Label>Entity Code:</Label>
                                    </Col>
                                    <Col md="8">
                                        <Label>{entityCode}</Label>
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row>
                                    <Col md="4" style={{ textAlign: "left" }}>
                                        <Label>Entity Name:</Label>
                                    </Col>
                                    <Col md="8">
                                        <Input type="text" value={entityName}
                                            onChange={(e: any) => setEntityName(e.currentTarget.value)}
                                            disabled={nameChangeDate === null}
                                        />
                                        <Row>
                                            <Col>
                                                <p>You must choose an effective date before editing the name.</p>
                                            </Col>
                                            <Col>
                                                <DatePicker
                                                    selected={nameChangeDate}
                                                    dateFormat="MM/dd/yyyy"
                                                    onChange={handleNameChangeDate}
                                                    maxDate={new Date()}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row>
                                    <Col md="4" style={{ textAlign: "left" }}>
                                        <Label>Entity Type Code:</Label>
                                    </Col>
                                    <Col md="8">
                                        <Label>{utilityHelper.SnakeCaseToTitleCase(entityTypeCode)}</Label>
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row>
                                    <Col md="4" style={{ textAlign: "left" }}>
                                        <Label>Creation Date:</Label>
                                    </Col>
                                    <Col md="8">
                                        <Label>{createDate}</Label>
                                    </Col>
                                </Row>
                            </FormGroup>
                            {
                                entitySelector && entitySelector.code > "3" && entitySelector.code < "9" &&
                                <FormGroup>
                                    <Row>
                                        <Col md="4" style={{ textAlign: "left" }}>
                                            <Label>District Type:</Label>
                                            </Col>
                                            <Col md="8">
                                            {districtType && districtType === "Community Redevelopment Agencies"
                                                ?
                                                    <Label>{districtType}</Label>
                                                :
                                                    <DropdownTreeSelect
                                                        data={districtTypeOptions}
                                                        keepTreeOnSearch={true}
                                                        texts={{ placeholder: "Select District Type..." }}
                                                        mode={"radioSelect"}
                                                        keepChildrenOnSearch={true}
                                                        onChange={handleDistrictTypeChange}
                                                    />
                                            }
                                            </Col>
                                    </Row>
                                </FormGroup>
                            }

                            {creationDocumentLabelRender}

                            {statutoryAuthorityLabelRender}

                            <FormGroup>
                                <Row>
                                    <Col>
                                        {/*TODO add change detection to submit button validation*/}
                                        <ReportAccessEnabler
                                            parentAfrEnabled={parentAfrEnabled}
                                            setParentAfrEnabled={setParentAfrEnabled}
                                            parentCourtReporting939Enabled={parentCourtReporting939Enabled}
                                            setParentCourtReporting939Enabled={setParentCourtReporting939Enabled}
                                            parentCourtReporting29Enabled={parentCourtReporting29Enabled}
                                            setParentCourtReporting29Enabled={setParentCourtReporting29Enabled}
                                            parentAfrReportAccessDate={parentAfrReportAccessDate}
                                            setParentAfrReportAccessDate={setParentAfrReportAccessDate}
                                            parentNineThirtyNineReportAccessDate={parentNineThirtyNineReportAccessDate}
                                            setParentNineThirtyNineReportAccessDate={setParentNineThirtyNineReportAccessDate}
                                            parentTwentyNineReportAccessDate={parentTwentyNineReportAccessDate}
                                            setParentTwentyNineReportAccessDate={setParentTwentyNineReportAccessDate}
                                            setParentReportAccessHasChanged={setParentReportAccessHasChanged}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>
                            {entitySelector && entitySelector.code > "3" && entitySelector.code < "9" && entitySelector.districtDependency && entitySelector.districtDependency !== 'NONE' &&
                                <FormGroup>
                                    <Row>
                                        <Col md="4" style={{ textAlign: "left" }}>
                                            <Label>District Dependency:</Label>
                                        </Col>
                                        <Col md="8">
                                            <Row>
                                                <Col>
                                                    <Label className="ml-4" check>
                                                        <Input type="radio"
                                                            name="ind"
                                                            value={"INDEPENDENT"}
                                                            onChange={handleDepStatusChange}
                                                            disabled={depStatusChangeDate === null}
                                                            checked={districtDependencyStatus === "INDEPENDENT"}
                                                        /><b>Independent</b>
                                                    </Label>
                                                </Col>
                                                <Col>
                                                    <Label className="ml-4" check>
                                                        <Input type="radio"
                                                            name="dep"
                                                            value={"DEPENDENT"}
                                                            onChange={handleDepStatusChange}
                                                            disabled={depStatusChangeDate === null}
                                                            checked={districtDependencyStatus === "DEPENDENT"}
                                                        /><b>Dependent</b>
                                                    </Label>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <p>You must choose an effective date before editing the District Dependency Code.</p>
                                                </Col>
                                                <Col>
                                                    <DatePicker
                                                        selected={depStatusChangeDate}
                                                        dateFormat="MM/dd/yyyy"
                                                        onChange={handleDepStatusChangeDate}
                                                        maxDate={new Date()}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            }
                            <FormGroup>
                                <Row>
                                    <Col md="4" style={{ textAlign: "left" }}>
                                        <Label>Primary Government:</Label>
                                    </Col>
                                    <Col md="8" >
                                        <Select
                                            value={selectedPrimary}
                                            onChange={handlePrimaryChange}
                                            options={primaryOptions}
                                            isDisabled={primaryChangeDate === null}
                                        />
                                        <Row>
                                            <Col>
                                                <p>You must choose an effective date before selecting a new primary.</p>
                                            </Col>
                                            <Col>
                                                <DatePicker
                                                    selected={primaryChangeDate}
                                                    dateFormat="MM/dd/yyyy"
                                                    onChange={handlePrimaryChangeDate}
                                                    maxDate={new Date()}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </FormGroup>
                            <Collapse isOpen={shouldShowDoingBusinessAs == true}>

                                <FormGroup>
                                    <Row>
                                        <Col md="4" style={{ textAlign: "left" }}>
                                            <Label>{doingBusinessAs}</Label>
                                        </Col>
                                        <Col md="7" >

                                            {hideSelectedOptions == true ?
                                                <Label>{selectedDoingBusinessAs.label}</Label>
                                                :
                                                <Select
                                                    value={selectedDoingBusinessAs}
                                                    onChange={handleDoingBusinessAsChange}
                                                    options={doingBusinessAsOptions}
                                                    
                                                />
                                            }
                                                 
                                        </Col>
                                    </Row>
                                </FormGroup>
                            </Collapse>

                        </Col>
                        <Col md="6">

                            <FormGroup>
                                <Row>
                                    <Col>
                                        <AddressEditor
                                            street={street}
                                            setStreet={setStreet}
                                            zipCode={zipCode}
                                            setZipCode={setZipCode}
                                            poBox={pobox}
                                            setPoBox={setPobox}
                                            cityCode={cityCode}
                                            setCityCode={setCityCode}
                                            county={county}
                                            setCounty={setCounty}
                                            state={state}
                                            setState={setState}
                                            canEdit={true}
                                            stateRestriction={"Florida"}
                                            servesInCountyCodes={servesInCountyCodes}
                                            setServesInCountyCodes={setServesInCountyCodes}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row>
                                    <Col>
                                        <FiscalYearEditor
                                            fiscalMonth={fiscalMonth}
                                            setFiscalMonth={setFiscalMonth}
                                            fiscalDay={fiscalDay}
                                            setFiscalDay={setFiscalDay}
                                            readyForUse={true}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row>
                                    <Col>
                                        <EntityStatusEditor
                                            status={entityStatus}
                                            statusReason={statusReason}
                                            setStatusReason={setStatusReason}
                                            setEntityStatus={setEntityStatus}
                                            dissolvedDate={dissolvedDate}
                                            setDissolvedDate={setDissolvedDate}
                                            inactiveDate={inactiveDate}
                                            setInactiveDate={setInactiveDate}
                                            reactivateDate={reactivateDate}
                                            setReactivateDate={setReactivateDate}
                                            renamedDate={renamedDate}
                                            setRenamedDate={setRenamedDate}
                                            mergedDate={mergedDate}
                                            setMergedDate={setMergedDate}
                                            readyForUse={mergedEntityStatusDisabled}
                                            setReadyForUse={setMergedEntityStatusDisabled}
                                            canReactivate={true}
                                            enableAllEntityStatus={true}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4"></Col>
                        <Col>
                            <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                type="grow" color="warning"
                                hidden={isEntityUpdateSaving == false} />

                            <div hidden={isEntityUpdateSaving == true}>
                                <Button type="button" onClick={saveEntity}>
                                    Save Changes</Button>
                            </div>

                        </Col>
                        <Col md="4"></Col>
                    </Row>
                </TabPane>

                <TabPane tabId="3">

                    <EntityHistoryViewer />

                </TabPane>
                <TabPane tabId="4">
                    <Row>
                        <Col sm="12">
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <EntityCreator
                                setActiveTab={setActiveTab}
                            />
                        </Col>
                    </Row>
                </TabPane>
                <TabPane tabId="5">
                    <Row>
                        <Col sm="12">

                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <DistrictTypeManager />
                        </Col>
                    </Row>
                </TabPane>
            </TabContent>
        </Page>
    );
}

export default EntityManagementAdmin;



